import React, { useState, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import {
  HomeIcon,
  BuildingOfficeIcon,
  RocketLaunchIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  DocumentChartBarIcon,
  UsersIcon,
  ServerIcon,
  ClipboardDocumentListIcon,
  CogIcon,
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  BuildingOfficeIcon as BuildingOfficeIconSolid,
  RocketLaunchIcon as RocketLaunchIconSolid,
  CalendarIcon as CalendarIconSolid,
  ArrowDownTrayIcon as ArrowDownTrayIconSolid,
  DocumentChartBarIcon as DocumentChartBarIconSolid,
  UsersIcon as UsersIconSolid,
  ServerIcon as ServerIconSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
} from '@heroicons/react/24/solid';

/**
 * Modern Vertical Sidebar Navigation
 * - Fixed position with icons
 * - Expandable on hover to show labels
 * - Settings at the bottom
 * - Market-standard design (Notion, Linear, Vercel style)
 */

function ModernSidebar({ user, onLogout }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const isAdmin = user?.role === 'Admin';
  const isManager = user?.role === 'Manager' || isAdmin;
  const isClient = user?.role === 'Client';

  // Define navigation items with icons
  const navItems = useMemo(() => {
    if (isClient) {
      return [
        { to: '/reports', label: 'Reports', Icon: DocumentChartBarIcon, IconSolid: DocumentChartBarIconSolid },
      ];
    }

    const items = [
      { to: '/', label: 'Dashboard', Icon: HomeIcon, IconSolid: HomeIconSolid, end: true },
      { to: '/companies', label: 'Companies', Icon: BuildingOfficeIcon, IconSolid: BuildingOfficeIconSolid },
      { to: '/journey', label: 'Company Setup', Icon: RocketLaunchIcon, IconSolid: RocketLaunchIconSolid },
    ];

    if (isManager) {
      items.push({ to: '/planning', label: 'Planning', Icon: CalendarIcon, IconSolid: CalendarIconSolid });
    }

    items.push(
      { to: '/downloads', label: 'Downloads', Icon: ArrowDownTrayIcon, IconSolid: ArrowDownTrayIconSolid },
      { to: '/reports', label: 'Reports', Icon: DocumentChartBarIcon, IconSolid: DocumentChartBarIconSolid }
    );

    if (isAdmin) {
      items.push(
        { to: '/users', label: 'Users', Icon: UsersIcon, IconSolid: UsersIconSolid },
        { to: '/admin/services', label: 'Services', Icon: ServerIcon, IconSolid: ServerIconSolid },
        { to: '/admin/audit-log', label: 'Audit Log', Icon: ClipboardDocumentListIcon, IconSolid: ClipboardDocumentListIconSolid }
      );
    }

    return items;
  }, [isAdmin, isManager, isClient]);

  function SidebarNavItem({ to, label, Icon, IconSolid, end = false }) {
    return (
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
            isActive
              ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50'
          }`
        }
      >
        {({ isActive }) => (
          <>
            <div className="shrink-0">
              {isActive ? (
                <IconSolid className="w-6 h-6" />
              ) : (
                <Icon className="w-6 h-6 group-hover:scale-110 transition-transform" />
              )}
            </div>
            <span
              className={`text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 absolute left-12'
              }`}
            >
              {label}
            </span>
            {/* Tooltip for collapsed state */}
            {!isExpanded && (
              <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none whitespace-nowrap z-50">
                {label}
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-800"></div>
              </div>
            )}
          </>
        )}
      </NavLink>
    );
  }

  return (
    <div
      className={`fixed top-0 left-0 h-screen bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col z-50 transition-all duration-300 ${
        isExpanded ? 'w-64' : 'w-20'
      }`}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Logo / Brand */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-200 dark:border-gray-800">
        <img src="/mibot-logo.png" alt="MiBOT" className="h-10 w-10 rounded-xl shrink-0" />
        <div
          className={`leading-tight transition-all duration-200 ${
            isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 absolute left-16'
          }`}
        >
          <div className="text-gray-900 dark:text-white font-bold text-base">MiBOT</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Onboarding</div>
        </div>
      </div>

      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2 space-y-1">
        {navItems.map((item) => (
          <SidebarNavItem key={item.to} {...item} />
        ))}
      </div>

      {/* Bottom Section: Settings, Theme, User, Logout */}
      <div className="border-t border-gray-200 dark:border-gray-800 p-2 space-y-1">
        {/* Settings Button */}
        {(isAdmin || isManager) && (
          <button
            onClick={() => navigate('/settings')}
            className="group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-all duration-200 w-full"
          >
            <CogIcon className="w-6 h-6 shrink-0 group-hover:rotate-90 transition-transform duration-300" />
            <span
              className={`text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 absolute left-12'
              }`}
            >
              Settings
            </span>
            {/* Tooltip */}
            {!isExpanded && (
              <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none whitespace-nowrap z-50">
                Settings
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-800"></div>
              </div>
            )}
          </button>
        )}

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-all duration-200 w-full"
        >
          {isDarkMode ? (
            <SunIcon className="w-6 h-6 shrink-0 group-hover:rotate-180 transition-transform duration-500" />
          ) : (
            <MoonIcon className="w-6 h-6 shrink-0 group-hover:-rotate-12 transition-transform duration-500" />
          )}
          <span
            className={`text-sm font-medium whitespace-nowrap transition-all duration-200 ${
              isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 absolute left-12'
            }`}
          >
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </span>
          {/* Tooltip */}
          {!isExpanded && (
            <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none whitespace-nowrap z-50">
              {isDarkMode ? 'Switch to Light' : 'Switch to Dark'}
              <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-800"></div>
            </div>
          )}
        </button>

        {/* User Info (Expandable) */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold shrink-0 text-lg">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div
              className={`min-w-0 transition-all duration-200 ${
                isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 absolute left-16'
              }`}
            >
              <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {user?.name || 'User'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email || ''}</div>
            </div>
          </div>

          {/* Logout Button */}
          <button
            onClick={onLogout}
            className="group relative flex items-center gap-3 px-3 py-2.5 mt-1 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 w-full"
          >
            <ArrowRightOnRectangleIcon className="w-6 h-6 shrink-0 group-hover:translate-x-1 transition-transform" />
            <span
              className={`text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 absolute left-12'
              }`}
            >
              Logout
            </span>
            {/* Tooltip */}
            {!isExpanded && (
              <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none whitespace-nowrap z-50">
                Logout
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-800"></div>
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ModernSidebar;

